<template>
  <v-card outlined class="dc-series" :class="{ 'dc-series--warning': hasWarning }">
    <div class="d-flex align-start px-4 pt-3">
      <div class="dc-series__heading">
        <v-tooltip bottom open-delay="400">
          <template v-slot:activator="{ on }">
            <div class="dc-series__title" v-on="on">{{ title || $t('components.dataCheck.DataCheckSeriesCard.unnamed') }}</div>
          </template>
          <span>{{ title || $t('components.dataCheck.DataCheckSeriesCard.unnamed') }}</span>
        </v-tooltip>
        <div class="dc-series__meta">
          <span v-if="unit">{{ unit }} · </span>{{ isMean ? $t('components.dataCheck.DataCheckSeriesCard.meanShort') : $t('components.dataCheck.DataCheckSeriesCard.sumShort') }}
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="text-right pl-3">
        <div class="dc-series__label">{{ isMean ? $t('components.dataCheck.DataCheckSeriesCard.average') : $t('components.dataCheck.DataCheckSeriesCard.total') }}</div>
        <div class="dc-series__value">{{ totalText }}</div>
      </div>
    </div>
    <div v-if="flags.length" class="px-4 pt-2 d-flex flex-wrap dc-series__flags">
      <v-tooltip v-for="flag in flags" :key="flag.code" bottom max-width="320">
        <template v-slot:activator="{ on }">
          <v-chip
            x-small
            label
            v-on="on"
            :color="flag.severity === 'warning' ? 'orange lighten-4' : 'blue-grey lighten-5'"
            :text-color="flag.severity === 'warning' ? 'orange darken-4' : 'blue-grey darken-2'"
          >
            <v-icon x-small left>{{ flag.severity === 'warning' ? 'mdi-alert' : 'mdi-information-outline' }}</v-icon>
            {{ $t(`components.dataCheck.DataCheckFlags.${flag.code}Label`) }}
          </v-chip>
        </template>
        <span>{{ $t(`components.dataCheck.DataCheckFlags.${flag.code}Desc`, { count: flag.count }) }}</span>
      </v-tooltip>
    </div>
    <div v-if="hasData" class="px-1">
      <v-chart :option="options" :init-options="{ renderer: 'canvas' }" autoresize style="height: 150px; width: 100%;"/>
    </div>
    <div v-else class="dc-series__empty caption grey--text">
      {{ $t('components.dataCheck.DataCheckSeriesCard.noData') }}
    </div>
  </v-card>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/bar'
import 'echarts/lib/chart/line'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/markArea'
import 'zrender/lib/canvas/canvas'
import { formatValue } from '@/utils/dataCheck'

const NEGATIVE_COLOR = '#E53935'
const EVENT_COLOR = 'rgba(255, 179, 0, 0.14)'

export default {
  props: {
    title: {
      type: String,
      default: null
    },
    unit: {
      type: String,
      default: ''
    },
    isMean: {
      type: Boolean,
      default: false
    },
    periods: {
      type: Array,
      required: true
    },
    values: {
      type: Array,
      required: true
    },
    total: {
      type: Number,
      default: null
    },
    flags: {
      type: Array,
      default: () => []
    },
    events: {
      type: Array,
      default: () => []
    },
    color: {
      type: String,
      default: '#4DB6E7'
    }
  },
  computed: {
    hasWarning () {
      return this.flags.some(flag => flag.severity === 'warning')
    },
    hasData () {
      return this.values.some(v => v !== null)
    },
    totalText () {
      return formatValue(this.total)
    },
    markArea () {
      if (!this.events.length) return undefined
      return {
        silent: true,
        itemStyle: { color: EVENT_COLOR },
        data: this.events.map(event => [{ xAxis: event.start }, { xAxis: event.end }])
      }
    },
    seriesOption () {
      if (this.isMean) {
        const points = this.values.filter(v => v !== null).length
        return {
          type: 'line',
          data: this.values,
          connectNulls: false,
          showSymbol: points < 60,
          symbolSize: 4,
          lineStyle: { width: 2, color: this.color },
          itemStyle: { color: this.color },
          markArea: this.markArea
        }
      }
      return {
        type: 'bar',
        barMaxWidth: 14,
        data: this.values.map(v => (v !== null && v < 0 ? { value: v, itemStyle: { color: NEGATIVE_COLOR } } : v)),
        itemStyle: { color: this.color, borderRadius: [2, 2, 0, 0] },
        markArea: this.markArea
      }
    },
    options () {
      return {
        animation: false,
        grid: { left: 46, right: 12, top: 12, bottom: 24 },
        xAxis: {
          type: 'category',
          data: this.periods,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#E0E0E0' } },
          axisLabel: { fontSize: 10, color: '#9E9E9E', hideOverlap: true }
        },
        yAxis: {
          type: 'value',
          splitNumber: 2,
          axisLabel: { fontSize: 10, color: '#9E9E9E', formatter: formatValue },
          splitLine: { lineStyle: { color: '#F0F0F0' } }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: this.isMean ? 'line' : 'shadow' },
          formatter: this.formatTooltip
        },
        series: [this.seriesOption]
      }
    }
  },
  methods: {
    formatTooltip (params) {
      const param = params[0]
      const value = param.value === null || param.value === undefined || param.value === '-'
        ? `<span style="color: #9E9E9E">${this.$t('components.dataCheck.DataCheckSeriesCard.noDataPeriod')}</span>`
        : `<b>${formatValue(param.value)}</b>${this.unit ? ` <span style="color: #9E9E9E">${this.unit}</span>` : ''}`
      return `<div style="font-size: 12px">${param.name}</div><div style="font-size: 14px">${value}</div>`
    }
  },
  components: {
    'v-chart': ECharts
  }
}
</script>

<style scoped>
.dc-series {
  border-left-width: 3px !important;
  height: 100%;
}
.dc-series--warning {
  border-left-color: #FB8C00 !important;
}
.dc-series__heading {
  min-width: 0;
  flex: 1 1 auto;
}
.dc-series__title {
  font-weight: 500;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dc-series__meta {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
}
.dc-series__label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.5);
}
.dc-series__value {
  font-size: 1.05rem;
  font-weight: 500;
}
.dc-series__flags {
  gap: 4px;
}
.dc-series__empty {
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
